import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardList, Plus, Pencil, Archive, RotateCcw, ArrowUp, ArrowDown, Trash2, Save,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { useSession } from '../lib/SessionContext';
import {
  Card, Button, Input, Select, Field, Badge, Alert, Spinner, EmptyState, Modal, Textarea,
} from '../components/ui';
import { QUESTION_KINDS, blankQuestion, withRange, formProblem, maxTotal, scaleTop } from '../lib/forms';
import StandardFormText from '../components/StandardFormText';

/**
 * A professional's assessment forms (migration 0065).
 *
 * Their own questionnaires, which only they see and send, and the standard
 * forms the practice offers. Sending and filling in happen in each patient's
 * clinical history; this is where questionnaires are written, and where admin
 * pastes the standard forms' official wording.
 *
 * Editing a questionnaire changes only what is sent from then on: every send
 * keeps a copy of the form as it was answered.
 */

const EMPTY_FORM = {
  id: null, title: '', description: '', in_session_only: false,
  questions: [blankQuestion('scale')], bands: [], scales: [],
};

const QuestionEditor = ({ question, index, count, onChange, onMove, onRemove }) => {
  const setMin = (value) => {
    const min = Math.max(0, Math.min(9, Number(value) || 0));
    onChange(withRange(question, min, Math.max(min + 1, Number(question.max))));
  };
  const setMax = (value) => {
    const max = Math.max(1, Math.min(10, Number(value) || 1));
    onChange(withRange(question, Math.min(Number(question.min), max - 1), max));
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('forms.kindLabel')}>
          <Select
            className="w-40"
            value={question.kind}
            onChange={(event) => onChange({ ...blankQuestion(event.target.value), prompt: question.prompt })}
          >
            {QUESTION_KINDS.map((kind) => (
              <option key={kind} value={kind}>{t(`forms.kind.${kind}`)}</option>
            ))}
          </Select>
        </Field>
        <div className="ml-auto flex gap-1">
          <Button variant="ghost" onClick={() => onMove(-1)} disabled={index === 0} title={t('forms.moveUp')}>
            <ArrowUp size={14} />
          </Button>
          <Button variant="ghost" onClick={() => onMove(1)} disabled={index === count - 1} title={t('forms.moveDown')}>
            <ArrowDown size={14} />
          </Button>
          <Button variant="ghost" onClick={onRemove} disabled={count === 1} title={t('forms.removeQuestion')}>
            <Trash2 size={14} className="text-rose-600" />
          </Button>
        </div>
      </div>

      <Field label={t('forms.prompt', { n: index + 1 })}>
        <Input value={question.prompt} onChange={(event) => onChange({ ...question, prompt: event.target.value })} />
      </Field>

      {question.kind === 'choice' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">{t('forms.optionsHint')}</p>
          {(question.options ?? []).map((option, position) => (
            // The option's place in the list is what an answer points at, so
            // it is also its identity while editing.
            // eslint-disable-next-line react/no-array-index-key
            <div key={position} className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1">
                <Field label={t('forms.optionLabel', { n: position + 1 })}>
                  <Input
                    value={option.label}
                    onChange={(event) => {
                      const options = [...question.options];
                      options[position] = { ...option, label: event.target.value };
                      onChange({ ...question, options });
                    }}
                  />
                </Field>
              </div>
              <Field label={t('forms.optionScore')}>
                <Input
                  type="number" min="0" max="10" className="w-20"
                  value={option.score}
                  onChange={(event) => {
                    const options = [...question.options];
                    options[position] = { ...option, score: event.target.value };
                    onChange({ ...question, options });
                  }}
                />
              </Field>
              <Button
                variant="ghost"
                title={t('forms.removeOption')}
                disabled={(question.options ?? []).length <= 2}
                onClick={() => onChange({ ...question, options: question.options.filter((_, i) => i !== position) })}
              >
                <Trash2 size={14} className="text-rose-600" />
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            disabled={(question.options ?? []).length >= 10}
            onClick={() => onChange({ ...question, options: [...(question.options ?? []), { label: '', score: 0 }] })}
          >
            <Plus size={14} />{t('forms.addOption')}
          </Button>
        </div>
      )}

      {question.kind === 'scale' && (
        <>
          <div className="flex flex-wrap gap-3">
            <Field label={t('forms.scaleFrom')}>
              <Input type="number" min="0" max="9" className="w-20" value={question.min}
                onChange={(event) => setMin(event.target.value)} />
            </Field>
            <Field label={t('forms.scaleTo')}>
              <Input type="number" min="1" max="10" className="w-20" value={question.max}
                onChange={(event) => setMax(event.target.value)} />
            </Field>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {question.labels.map((label, offset) => (
              // A scale's labels are its values in order; the value is the key.
              <Field key={Number(question.min) + offset} label={t('forms.labelFor', { value: Number(question.min) + offset })}>
                <Input
                  value={label}
                  onChange={(event) => {
                    const labels = [...question.labels];
                    labels[offset] = event.target.value;
                    onChange({ ...question, labels });
                  }}
                />
              </Field>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FormEditor = ({ initial, onClose, onSaved }) => {
  // Each question carries a key of its own while editing, so moving one does
  // not hand its inputs to its neighbour. It is not saved.
  const [form, setForm] = useState(() => ({
    ...initial,
    questions: initial.questions.map((question) => ({ ...question, key: Math.random().toString(36).slice(2) })),
    bands: initial.bands.map((band) => ({ ...band, key: Math.random().toString(36).slice(2) })),
    scales: (initial.scales ?? []).map((scale) => ({
      ...scale,
      items: (scale.items ?? []).join(', '),
      bands: (scale.bands ?? []).map((band) => ({ ...band, key: Math.random().toString(36).slice(2) })),
      key: Math.random().toString(36).slice(2),
    })),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const top = maxTotal(form.questions);

  const setQuestions = (questions) => setForm({ ...form, questions });
  const addQuestion = (kind) => setQuestions([
    ...form.questions, { ...blankQuestion(kind), key: Math.random().toString(36).slice(2) },
  ]);
  const moveQuestion = (index, step) => {
    const questions = [...form.questions];
    const [moved] = questions.splice(index, 1);
    questions.splice(index + step, 0, moved);
    setQuestions(questions);
  };
  const setBands = (bands) => setForm({ ...form, bands });
  const setScales = (scales) => setForm({ ...form, scales });
  // "1, 6, 8" as typed becomes the numbers the form is saved with.
  const itemNumbers = (items) => String(items ?? '').split(',').map((part) => Number(part.trim()))
    .filter((number) => Number.isInteger(number) && number > 0);


  const save = async () => {
    setError(null);
    if (form.title.trim().length < 2) { setError(t('forms.titleRequired')); return; }
    const questions = form.questions.map((question) => {
      if (question.kind === 'scale') {
        return {
          kind: 'scale', prompt: question.prompt.trim(), min: Number(question.min), max: Number(question.max),
          labels: question.labels.map((label) => label.trim()),
        };
      }
      if (question.kind === 'choice') {
        return {
          kind: 'choice',
          prompt: question.prompt.trim(),
          options: (question.options ?? []).map((option) => ({
            label: String(option.label ?? '').trim(),
            score: option.score === '' ? '' : Number(option.score),
          })),
        };
      }
      return { kind: question.kind, prompt: question.prompt.trim() };
    });
    const bands = form.bands.map((band) => ({
      from: band.from === '' ? '' : Number(band.from),
      to: band.to === '' ? '' : Number(band.to),
      label: String(band.label ?? '').trim(),
    }));
    const scales = form.scales.map((scale) => ({
      label: String(scale.label ?? '').trim(),
      items: itemNumbers(scale.items),
      bands: (scale.bands ?? []).map((band) => ({
        from: band.from === '' ? '' : Number(band.from),
        to: band.to === '' ? '' : Number(band.to),
        label: String(band.label ?? '').trim(),
      })),
    }));
    const problem = formProblem(questions, bands, scales);
    if (problem) { setError(problem); return; }

    setSaving(true);
    const { error: saveError } = await supabase.rpc('save_my_form', {
      p_form_id: form.id,
      p_title: form.title.trim(),
      p_description: form.description.trim() || null,
      p_in_session_only: form.in_session_only,
      p_questions: questions,
      p_bands: bands,
      p_scales: scales,
    });
    setSaving(false);
    if (saveError) { setError(saveError.message); return; }
    onSaved();
  };

  return (
    <Modal
      open
      wide
      title={t(form.id ? 'forms.editTitle' : 'forms.newTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={save} loading={saving}><Save size={16} />{t('common.save')}</Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && <Alert tone="danger">{error}</Alert>}
        {form.id && <Alert tone="info">{t('forms.editKeepsSent')}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('forms.formTitle')} required>
            <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          </Field>
          <label className="flex items-start gap-2 self-end text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.in_session_only}
              onChange={(event) => setForm({ ...form, in_session_only: event.target.checked })}
            />
            <span>
              <span className="font-medium">{t('forms.inSessionOnly')}</span>
              <span className="block text-xs text-slate-500">{t('forms.inSessionOnlyHint')}</span>
            </span>
          </label>
        </div>
        <Field label={t('forms.description')} hint={t('forms.descriptionHint')}>
          <Textarea rows={2} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">{t('forms.questions')}</h3>
          {form.questions.map((question, index) => (
            <QuestionEditor
              key={question.key}
              question={question}
              index={index}
              count={form.questions.length}
              onChange={(changed) => setQuestions(form.questions.map((row, i) => (i === index ? { ...changed, key: row.key } : row)))}
              onMove={(step) => moveQuestion(index, step)}
              onRemove={() => setQuestions(form.questions.filter((_, i) => i !== index))}
            />
          ))}
          <div className="flex flex-wrap gap-2">
            {QUESTION_KINDS.map((kind) => (
              <Button key={kind} variant="secondary" onClick={() => addQuestion(kind)}>
                <Plus size={14} />{t(`forms.add.${kind}`)}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{t('forms.bands')}</h3>
            <p className="text-xs text-slate-500">{t('forms.bandsHint', { max: top })}</p>
          </div>
          {form.bands.map((band, index) => (
            <div key={band.key} className="flex flex-wrap items-end gap-2">
              <Field label={t('forms.bandFrom')}>
                <Input type="number" min="0" className="w-20" value={band.from}
                  onChange={(event) => setBands(form.bands.map((row, i) => (i === index ? { ...row, from: event.target.value } : row)))} />
              </Field>
              <Field label={t('forms.bandTo')}>
                <Input type="number" min="0" className="w-20" value={band.to}
                  onChange={(event) => setBands(form.bands.map((row, i) => (i === index ? { ...row, to: event.target.value } : row)))} />
              </Field>
              <div className="min-w-0 flex-1">
                <Field label={t('forms.bandLabel')}>
                  <Input value={band.label}
                    onChange={(event) => setBands(form.bands.map((row, i) => (i === index ? { ...row, label: event.target.value } : row)))} />
                </Field>
              </div>
              <Button variant="ghost" onClick={() => setBands(form.bands.filter((_, i) => i !== index))} title={t('forms.removeBand')}>
                <Trash2 size={14} className="text-rose-600" />
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            onClick={() => setBands([...form.bands, { from: '', to: '', label: '', key: Math.random().toString(36).slice(2) }])}
          >
            <Plus size={14} />{t('forms.addBand')}
          </Button>
        </div>

        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{t('forms.scalesTitle')}</h3>
            <p className="text-xs text-slate-500">{t('forms.scalesHint')}</p>
          </div>
          {form.scales.map((scale, index) => {
            const chosen = itemNumbers(scale.items);
            return (
              <div key={scale.key} className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <Field label={t('forms.scaleName')}>
                      <Input
                        value={scale.label}
                        onChange={(event) => setScales(form.scales.map((row, i) => (
                          i === index ? { ...row, label: event.target.value } : row)))}
                      />
                    </Field>
                  </div>
                  <Button
                    variant="ghost"
                    title={t('forms.removeScale')}
                    onClick={() => setScales(form.scales.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={14} className="text-rose-600" />
                  </Button>
                </div>
                <Field label={t('forms.scaleItems')} hint={t('forms.scaleItemsHint')}>
                  <Input
                    value={scale.items}
                    onChange={(event) => setScales(form.scales.map((row, i) => (
                      i === index ? { ...row, items: event.target.value } : row)))}
                  />
                </Field>
                <p className="text-xs text-slate-500">
                  {t('forms.scaleMax', { max: scaleTop(form.questions, chosen) })}
                </p>

                <p className="text-xs font-medium text-slate-700">{t('forms.scaleBandsTitle')}</p>
                {(scale.bands ?? []).map((band, bandIndex) => (
                  <div key={band.key} className="flex flex-wrap items-end gap-2">
                    <Field label={t('forms.bandFrom')}>
                      <Input type="number" min="0" className="w-20" value={band.from}
                        onChange={(event) => setScales(form.scales.map((row, i) => (i === index ? {
                          ...row,
                          bands: row.bands.map((b, j) => (j === bandIndex ? { ...b, from: event.target.value } : b)),
                        } : row)))} />
                    </Field>
                    <Field label={t('forms.bandTo')}>
                      <Input type="number" min="0" className="w-20" value={band.to}
                        onChange={(event) => setScales(form.scales.map((row, i) => (i === index ? {
                          ...row,
                          bands: row.bands.map((b, j) => (j === bandIndex ? { ...b, to: event.target.value } : b)),
                        } : row)))} />
                    </Field>
                    <div className="min-w-0 flex-1">
                      <Field label={t('forms.bandLabel')}>
                        <Input value={band.label}
                          onChange={(event) => setScales(form.scales.map((row, i) => (i === index ? {
                            ...row,
                            bands: row.bands.map((b, j) => (j === bandIndex ? { ...b, label: event.target.value } : b)),
                          } : row)))} />
                      </Field>
                    </div>
                    <Button
                      variant="ghost"
                      title={t('forms.removeBand')}
                      onClick={() => setScales(form.scales.map((row, i) => (i === index ? {
                        ...row, bands: row.bands.filter((_, j) => j !== bandIndex),
                      } : row)))}
                    >
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  onClick={() => setScales(form.scales.map((row, i) => (i === index ? {
                    ...row,
                    bands: [...(row.bands ?? []), { from: '', to: '', label: '', key: Math.random().toString(36).slice(2) }],
                  } : row)))}
                >
                  <Plus size={14} />{t('forms.addBand')}
                </Button>
              </div>
            );
          })}
          <Button
            variant="secondary"
            onClick={() => setScales([...form.scales, { label: '', items: '', bands: [], key: Math.random().toString(36).slice(2) }])}
          >
            <Plus size={14} />{t('forms.addScale')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const FormRow = ({ form, children }) => (
  <li className="flex flex-wrap items-start justify-between gap-3 py-3">
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{form.title}</span>
        {form.in_session_only && <Badge tone="info">{t('forms.inSessionOnly')}</Badge>}
        {!form.is_active && <Badge tone="neutral">{t('forms.archived')}</Badge>}
        {!form.ready && <Badge tone="warning">{t('forms.notReady')}</Badge>}
      </div>
      <p className="text-xs text-slate-500">
        {t('forms.questionsCount', { count: form.questions.length })}
        {form.description ? ` · ${form.description}` : ''}
      </p>
    </div>
    {children && <div className="flex gap-1">{children}</div>}
  </li>
);

const FormsView = () => {
  const { isAdmin } = useSession();
  const [forms, setForms] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data, error: loadError } = await supabase.rpc('my_forms');
    if (loadError) setError(loadError.message);
    setForms(data ?? []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const own = useMemo(() => (forms ?? []).filter((form) => form.is_own), [forms]);
  const standard = useMemo(() => (forms ?? []).filter((form) => !form.is_own), [forms]);

  const setActive = async (form, active) => {
    setError(null);
    const { error: activeError } = await supabase.rpc('set_my_form_active', { p_form_id: form.id, p_active: active });
    if (activeError) { setError(activeError.message); return; }
    load();
  };

  if (!forms) return <Spinner />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <ClipboardList size={24} className="text-slate-400" />{t('forms.title')}
          </h1>
          <p className="text-sm text-slate-500">{t('forms.subtitle')}</p>
        </div>
        <Button onClick={() => setEditing(EMPTY_FORM)}><Plus size={16} />{t('forms.newForm')}</Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <Card title={t('forms.own')}>
        {own.length === 0 ? (
          <EmptyState icon={ClipboardList} title={t('forms.ownNone')} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {own.map((form) => (
              <FormRow key={form.id} form={form}>
                <Button variant="ghost" onClick={() => setEditing(form)} title={t('forms.edit')}><Pencil size={14} /></Button>
                {form.is_active ? (
                  <Button variant="ghost" onClick={() => setActive(form, false)} title={t('forms.archive')}><Archive size={14} /></Button>
                ) : (
                  <Button variant="ghost" onClick={() => setActive(form, true)} title={t('forms.restore')}><RotateCcw size={14} /></Button>
                )}
              </FormRow>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t('forms.standard')} subtitle={t('forms.standardHint')}>
        <ul className="divide-y divide-slate-100">
          {standard.map((form) => <FormRow key={form.id} form={form} />)}
        </ul>
      </Card>

      {/* The official wording of PHQ-9 and GAD-7, pasted once by admin (0065),
          next to the forms it belongs to. */}
      {isAdmin && <StandardFormText onSaved={load} />}

      {editing && (
        <FormEditor
          initial={{
            ...EMPTY_FORM,
            ...editing,
            description: editing.description ?? '',
          }}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setNotice(t('forms.saved')); load(); }}
        />
      )}
    </div>
  );
};

export default FormsView;
